import { Alert, Button, FileButton, Group, Image, Modal, SimpleGrid, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconCamera, IconTrash } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki, type Shema } from '../api/odjemalec';
import { useSlikaApi } from '../api/useSlikaApi';
import { casIzMs } from '../oblika/oblika';

type Nalog = Shema['DelovniNalogPodrobnosti'];
type Slika = Shema['SlikaNaloga'];

/**
 * Fotografije naloga (poškodbe ob sprejemu, zamenjani deli): na telefonu »Fotografiraj« odpre kamero, na računalniku
 * izbiro datotek; več slik naenkrat. Strežnik jih pomanjša; klik na sličico jo poveča. Po prevzemu samo ogled.
 */
export function SlikeNaloga({ nalog, posodobljen }: { nalog: Nalog; posodobljen: (n: Nalog) => void }) {
  const [povecana, setPovecana] = useState<Slika | null>(null);
  const [napake, setNapake] = useState<string[]>([]);
  const nalozi = useMutation({
    mutationFn: async (datoteke: File[]) => {
      const nove: string[] = [];
      for (const f of datoteke) {
        try {
          const telo = new FormData();
          telo.append('slika', f);
          posodobljen(
            await podatki(
              api.POST('/api/delavnica/nalogi/{id}/slike', {
                params: { path: { id: nalog.id! } },
                // multipart: FormData gre, kot je (brskalnik doda mejo); tip v shemi je opis polja
                body: telo as unknown as { slika: string },
              }),
            ),
          );
        } catch (e) {
          nove.push(`${f.name}: ${e instanceof NapakaApi && e.polja.slika ? e.polja.slika : sporocilo(e)}`);
        }
      }
      setNapake(nove);
    },
  });
  const izbrisi = useMutation({
    mutationFn: (slikaId: number) =>
      podatki(
        api.DELETE('/api/delavnica/nalogi/{id}/slike/{slikaId}', { params: { path: { id: nalog.id!, slikaId } } }),
      ),
    onSuccess: (n) => {
      setPovecana(null);
      posodobljen(n);
    },
  });
  const slike = nalog.slike ?? [];
  return (
    <Stack gap="sm">
      {slike.length === 0 ? (
        <Text size="sm" c="dimmed">
          Ni fotografij.
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 3, sm: 5, md: 6 }} spacing="xs">
          {slike.map((s) => (
            <MalaSlika key={s.id} nalogId={nalog.id!} slika={s} odpri={() => setPovecana(s)} />
          ))}
        </SimpleGrid>
      )}
      {nalog.urejanje && (
        <Group>
          <FileButton
            onChange={(f) => f.length > 0 && nalozi.mutate(f)}
            accept="image/jpeg,image/png"
            multiple
            capture="environment"
          >
            {(props) => (
              <Button {...props} variant="light" leftSection={<IconCamera size={18} />} loading={nalozi.isPending}>
                Fotografiraj
              </Button>
            )}
          </FileButton>
        </Group>
      )}
      {napake.length > 0 && (
        <Alert color="red" variant="light" role="alert">
          {napake.join(' ')}
        </Alert>
      )}
      <Modal opened={!!povecana} onClose={() => setPovecana(null)} title="Fotografija" size="xl">
        {povecana && (
          <Stack gap="sm">
            <VelikaSlika nalogId={nalog.id!} slika={povecana} />
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {[povecana.opis, povecana.ustvaril, casIzMs(povecana.ustvarjeno)].filter(Boolean).join(' · ')}
              </Text>
              {nalog.urejanje && (
                <Button
                  color="red"
                  variant="light"
                  leftSection={<IconTrash size={16} />}
                  onClick={() => izbrisi.mutate(povecana.id!)}
                  loading={izbrisi.isPending}
                >
                  Izbriši
                </Button>
              )}
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

function MalaSlika({ nalogId, slika, odpri }: { nalogId: number; slika: Slika; odpri: () => void }) {
  const url = useSlikaApi(`${nalogId}-${slika.id}-mala`, (signal) =>
    api.GET('/api/delavnica/nalogi/{id}/slike/{slikaId}', {
      params: { path: { id: nalogId, slikaId: slika.id! }, query: { mala: true } },
      parseAs: 'blob',
      signal,
    }),
  );
  return (
    <UnstyledButton onClick={odpri} aria-label={`Fotografija ${slika.opis ?? slika.id}`}>
      <Image src={url} radius="md" h={90} fit="cover" fallbackSrc="data:image/gif;base64,R0lGODlhAQABAAAAACw=" />
    </UnstyledButton>
  );
}

function VelikaSlika({ nalogId, slika }: { nalogId: number; slika: Slika }) {
  const url = useSlikaApi(`${nalogId}-${slika.id}-velika`, (signal) =>
    api.GET('/api/delavnica/nalogi/{id}/slike/{slikaId}', {
      params: { path: { id: nalogId, slikaId: slika.id! } },
      parseAs: 'blob',
      signal,
    }),
  );
  return url ? <Image src={url} radius="md" fit="contain" mah="70vh" /> : <Text c="dimmed">Nalagam …</Text>;
}
